import { HttpStatusCode } from '@angular/common/http';
import { Component, Inject, Injector, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import type { BodyComponentObject, ButtonItemComponentObject, ButtonsComponentObject, HeaderComponentObject, TemplateMessageObject } from '@trendbuild/trend-cloud-api/src/lib/models/graph-api/template-message';
import { AbstractComponent, applyWaTemplateParams, Attendance, buildAgentSignature, ConfirmationComponent, extractWaTemplateParameters, Message, MessageHelper, MessageScheduled, MessageScheduledService, MessageScheduledStatusEnum, MessageService, MessageTypeEnum, Pager, UtilHelper } from 'lib-trend-core';
import { ComponentType } from 'ngx-toastr';
import { BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ChatTemplateScheduleComponent } from '../../chat-template-schedule/chat-template-schedule.component';
import { ChatMessageScheduleComponent } from '../chat-message-schedule';

@Component({
    selector: 'message-schedule-list',
    // templateUrl: './chat-message-schedule-list.html',
    templateUrl: './chat-message-schedule-list.v2.component.html',
    styleUrls: ['./chat-message-schedule-list.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChatMessageScheduleListComponent extends AbstractComponent {
  public messagesScheduled: MessageScheduled[] = [];
  pager: Pager<MessageScheduled> = new Pager<MessageScheduled>({ perPage: 7 });
  selectedMessage: MessageScheduled | null = null;


  private loadingSpinnerSubject = new BehaviorSubject<boolean>(false);
  loadingSpinner$ = this.loadingSpinnerSubject.asObservable();
  selectedStatus: string = 'todos';
  public statusList: string[] = Object.values(MessageScheduledStatusEnum);

  constructor(
    injector: Injector,
    @Inject(MAT_DIALOG_DATA) public attendance: Attendance,
    private messageScheduledService: MessageScheduledService,
    private modalScheduleMessageList: MatDialog,
    private messageScgeduledService: MessageScheduledService,
    public messageService: MessageService,
  ) {
    super(injector);
  }

  ngOnInit() {
    this.getList();
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.getList(this.searchParams.status);
  }

  getList(status?: string) {
    this.loadingSpinnerSubject.next(true);
    this.searchParams = {
      company: super.getIDCurrentCompany(),
      contact: this.attendance.contact?._id,
    };

    if (status && status !== 'todos') {
      this.searchParams.status = status;
    }

    if (this.attendance.channel) {
      this.searchParams.channel = this.attendance.channel._id;
    }

    this.loadingSpinnerSubject.next(true);
    this.messageScheduledService.getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (pager: Pager<MessageScheduled>) => {
          this.pager = pager;
          this.messagesScheduled = pager.list;
          this.loadingSpinnerSubject.next(false);
        },
        error: (err) => {
          this.loadingSpinnerSubject.next(false);
        }
      });
  }

  formatTime(time: string): string {
    if (time && time.length === 4) {
      const hours = time.slice(0, 2);
      const minutes = time.slice(2, 4);
      return `${hours}:${minutes}`;
    }
    return time;
  }
  selectMessageForEdit(messageScheduled: MessageScheduled): void {

    const dialogComponent = (messageScheduled.contentType === 'template'
      ? ChatTemplateScheduleComponent
      : ChatMessageScheduleComponent) as ComponentType<any>;

    const dialogConfig = {
      width: messageScheduled.contentType === 'template' ? '1000px' : '700px',
      height: messageScheduled.contentType === 'template' ? '870px' : undefined,
      data: { message: messageScheduled, attendance: this.attendance },
    };

    const dialogRef = this.modalScheduleMessageList.open(dialogComponent, dialogConfig);

    dialogRef.afterClosed().pipe().subscribe(() => {
      this.getList();
    });
  }

  deleteMessage(message: MessageScheduled): void {
    const dialogRef = this.modalScheduleMessageList.open(ConfirmationComponent, {
      width: '500px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.messageScgeduledService.delete(message._id).subscribe({
          next: (response) => {
            this.alertService.success(this.t('attendance.chatScheduleList.scheduledMessageDeleted'));
          },
          error: (error) => {
            this.alertService.error(this.t('attendance.chatScheduleList.deleteScheduledMessageError'));
            console.error(error);
          },
          complete: () => {
            this.getList()
          },
        });
      }
    });
  }

  triggerFlashAction(message: MessageScheduled): void {
    message.status = MessageScheduledStatusEnum.MANUAL;
    message.scheduledDate = new Date();

    this.messageScheduledService.update(message._id, message).subscribe({
      next: () => {
        if (message.type === MessageTypeEnum.button) {
          const buttonMessage = {
            from: this.attendance.channel.identifier,
            to: this.attendance.contact.phone ?? this.attendance.contact.whatsapp?.bsuid,
            content: message.content,
            contentType: message.contentType,
            contact: { _id: this.attendance.contact._id },
            channel: { _id: this.attendance.channel._id },
            company: { _id: this.attendance.company._id },
            attendance: { _id: this.attendance._id },
            user: { _id: super.getIDCurrentUser() },
            type: MessageTypeEnum.button,
            metadata: message.metadata,
            automated: false
          } as Message;
          this.createPostMessage(buttonMessage);
        } else if (message.contentType?.startsWith('text')) {
          this.replyText(this.attendance, message.content);
        } else {
          this.replyMedia(this.attendance, message.type, message.content, message.contentType, message.caption);
        }

        this.alertService.success(this.t('attendance.chatScheduleList.messageSentImmediately'));
        this.getList();
      },
      error: (err) => {
        this.alertService.error(this.t('attendance.chatScheduleList.sendMessageImmediatelyError'));
        console.error(err);
      },
    });
  }

  replyText(attendance: Attendance, text: string): void {
    this.createPostMessage(
      MessageHelper.createMessageFromAttendance(
        attendance,
        MessageTypeEnum.text,
        text,
        null,
        super.getIDCurrentUser()
      )
    );
  }

  private createPostMessage(message: Message): void {
    // RCS has no rich text, so the WhatsApp markers would reach the customer verbatim.
    const signature = buildAgentSignature(this.attendance?.channel?.type, this.getNameCurrentUser());
    if (message.type === MessageTypeEnum.text && super.getSignatureConversation() && !!signature) {
      message.content = `${signature} \n\n ${message.content}`;
    }
    this.messageService.create(message).subscribe({
      next: () => {
      },
      error: (err) => {
        // A send the wallet refused for lack of balance (HTTP 402) already had its reason
        // shown by the HTTP error interceptor: the generic failure on top of it would only
        // crowd the one message the person can act on.
        if (err?.status !== HttpStatusCode.PaymentRequired) {
          this.alertService.error(this.t('attendance.chatScheduleList.sendMessageError'));
        }
        console.error(err);
      }
    });
  }

  replyMedia(attendance: Attendance, type: MessageTypeEnum, content: string, contentType: string, caption?: string): void {
    if (this.attendance._id !== attendance._id) {
      this.alertService.warning(this.t('attendance.chatScheduleList.audioCanceledDueToAttendanceSwitch'));
      sessionStorage.removeItem(`preview_audio_${attendance._id}`);
      return;
    }

    const message = caption && (type === MessageTypeEnum.image || type === MessageTypeEnum.document || type === MessageTypeEnum.video)
      ? MessageHelper.createMessageFromAttendance(attendance, type, content, contentType, super.getIDCurrentUser(), caption)
      : MessageHelper.createMessageFromAttendance(attendance, type, content, contentType, super.getIDCurrentUser());

    this.createPostMessage(message);
  }


  onStatusChange(event: any): void {
    this.getList(event.value);
  }

  translateStatus(status: string): string {
    const statusKeys = {
      'WAITING': 'attendance.chatScheduleList.status.waiting',
      'DONE': 'attendance.chatScheduleList.status.done',
      'ERROR': 'attendance.chatScheduleList.status.error',
      'MANUAL': 'attendance.chatScheduleList.status.manual'
    };
    // resolved at render time — the catalog may not be loaded yet on ngOnInit
    return statusKeys[status] ? this.t(statusKeys[status]) : status;
  }

  formatWhatsappMessage(text: string): string {
    return UtilHelper.formatWhatsappMessage(text);
  }

  isTemplateMidia(messageScheduled: MessageScheduled) {
    const template = <TemplateMessageObject>messageScheduled.metadata.template;
    const headerComponent = <HeaderComponentObject>template?.components.find(c => c.type === 'HEADER');
    return (!!headerComponent && headerComponent!.format !== 'TEXT') ?? false;
  }

  isTemplateImage(messageScheduled: MessageScheduled): boolean {
    const template = <TemplateMessageObject>messageScheduled.metadata.template;
    const headerComponent = <HeaderComponentObject>template?.components.find(c => c.type === 'HEADER');
    return headerComponent?.format === 'IMAGE';
  }

  isTemplateDocument(messageScheduled: MessageScheduled): boolean {
    const template = <TemplateMessageObject>messageScheduled.metadata.template;
    const headerComponent = <HeaderComponentObject>template?.components.find(c => c.type === 'HEADER');
    return headerComponent?.format === 'DOCUMENT';
  }

  isTemplateVideo(messageScheduled: MessageScheduled): boolean {
    const template = <TemplateMessageObject>messageScheduled.metadata.template;
    const headerComponent = <HeaderComponentObject>template?.components.find(c => c.type === 'HEADER');
    return headerComponent?.format === 'VIDEO';
  }

  getContentTemplateMessage(messageScheduled: MessageScheduled): string {
    const template = messageScheduled.metadata.template as TemplateMessageObject;
    const bodyComponent = template?.components.find(c => c.type === 'BODY') as BodyComponentObject;
    if (!bodyComponent?.text) {
      return '';
    }

    // POSITIONAL params are matched by position ({{1}}, {{2}}…); NAMED ones by placeholder name.
    const params = messageScheduled.metadata.params ?? messageScheduled.metadata.bodyParams;
    const names = Array.isArray(params)
      ? params.map((_, index) => `${index + 1}`)
      : extractWaTemplateParameters(bodyComponent.text);
    const values = Array.isArray(params) ? params : names.map((name) => params?.[name]);

    return applyWaTemplateParams(bodyComponent.text, names, values);
  }

  getTemplateButtons(messageScheduled: MessageScheduled): Array<ButtonItemComponentObject> {
    const template = messageScheduled.metadata.template as TemplateMessageObject;
    if (template && template.components) {
      const buttonsComponent = template.components.find(component => component.type === 'BUTTONS') as ButtonsComponentObject;
      return buttonsComponent ? buttonsComponent.buttons : [];
    }

    return [];
  }
}
